// Template recipient types
export type ProjectTemplateRecipientType =
  | 'BROKER'
  | 'THIRD_PARTY_BROKER' // Downstream broker-to-broker flow
  | 'THIRD_PARTY_CONTRACTOR'
  | 'SUB_HAULER'
  | 'THIRD_PARTY_SUB_HAULER' // Downstream sub-hauler-to-sub-hauler flow

export type TagTemplateRecipientType =
  | 'BROKER_DRIVER'
  | 'THIRD_PARTY_CONTRACTOR_DRIVER'
  | 'CONTRACTOR_DRIVER'
  | 'SUB_HAULER_DRIVER'

// Sender stakeholder types
export type SenderStakeholderType = 'CONTRACTOR' | 'BROKER' | 'SUB_HAULER'

// Field types for template configuration
export type FieldType = 'search' | 'form' | 'readonly' | 'project_search'

// Template field configuration
export interface TemplateFieldConfig {
  label: string
  fieldType: FieldType
  fieldKey: string // The key used in form data
  required: boolean
  visibleTo?: SenderStakeholderType[] // Which sender stakeholder types can see this field
}

// Template configuration for Project Terms
// Each template shows: Contractor (search), Site Supervisor (form), Recipient Type (search)
export const PROJECT_TEMPLATE_CONFIG: Record<
  ProjectTemplateRecipientType,
  {
    title: string
    fields: TemplateFieldConfig[]
    recipientLabel: string // Label for the recipient type (Broker, Third Party Contractor, Sub Hauler)
    recipientFieldKey: string // The field key for the recipient search
    parentProjectRequired?: (stakeholderType: SenderStakeholderType) => boolean
  }
> = {
  BROKER: {
    title: 'Project Terms',
    fields: [
      {
        label: 'Contractor',
        fieldType: 'search',
        fieldKey: 'contractor_contacts',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Project Manager',
        fieldType: 'search',
        fieldKey: 'project_managers',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Administrator',
        fieldType: 'search',
        fieldKey: 'administrators',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Site Supervisor',
        fieldType: 'form',
        fieldKey: 'site_supervisor',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Broker',
        fieldType: 'search',
        fieldKey: 'broker_contacts',
        required: true,
      },
    ],
    recipientLabel: 'Broker',
    recipientFieldKey: 'broker_contacts',
  },
  THIRD_PARTY_BROKER: {
    title: 'Project Terms',
    fields: [
      // For broker downstream: parent project is optional
      {
        label: 'Parent Project',
        fieldType: 'project_search',
        fieldKey: 'parent_project_id',
        required: false,
        visibleTo: ['BROKER'],
      },
      {
        label: 'Original Sender',
        fieldType: 'readonly',
        fieldKey: 'original_sender',
        required: false,
        visibleTo: ['BROKER'],
      },
      // Recipient field
      {
        label: 'Third Party Broker',
        fieldType: 'search',
        fieldKey: 'broker_contacts',
        required: true,
      },
    ],
    recipientLabel: 'Third Party Broker',
    recipientFieldKey: 'broker_contacts',
    // Broker downstream parent project is optional
    parentProjectRequired: () => false,
  },
  THIRD_PARTY_CONTRACTOR: {
    title: 'Project Terms',
    fields: [
      {
        label: 'Contractor',
        fieldType: 'search',
        fieldKey: 'contractor_contacts',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Project Manager',
        fieldType: 'search',
        fieldKey: 'project_managers',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Administrator',
        fieldType: 'search',
        fieldKey: 'administrators',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Site Supervisor',
        fieldType: 'form',
        fieldKey: 'site_supervisor',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Third Party Contractor',
        fieldType: 'search',
        fieldKey: 'third_party_contractor_ids',
        required: true,
      },
    ],
    recipientLabel: 'Third Party Contractor',
    recipientFieldKey: 'third_party_contractor_ids',
  },
  SUB_HAULER: {
    title: 'Project Terms',
    fields: [
      // For contractors: requires contractor field
      {
        label: 'Contractor',
        fieldType: 'search',
        fieldKey: 'contractor_contacts',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Project Manager',
        fieldType: 'search',
        fieldKey: 'project_managers',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Administrator',
        fieldType: 'search',
        fieldKey: 'administrators',
        required: false,
        visibleTo: ['CONTRACTOR'],
      },
      {
        label: 'Site Supervisor',
        fieldType: 'form',
        fieldKey: 'site_supervisor',
        required: true,
        visibleTo: ['CONTRACTOR'],
      },
      // Common: recipient field
      {
        label: 'Sub Hauler',
        fieldType: 'search',
        fieldKey: 'sub_hauler_ids',
        required: true,
      },
    ],
    recipientLabel: 'Sub Hauler',
    recipientFieldKey: 'sub_hauler_ids',
    // Sub-haulers don't need parent project when sent by contractor/broker
    parentProjectRequired: () => false,
  },
  THIRD_PARTY_SUB_HAULER: {
    title: 'Project Terms',
    fields: [
      // Sub-hauler downstream: requires parent project selection
      {
        label: 'Parent Project',
        fieldType: 'project_search',
        fieldKey: 'parent_project_id',
        required: true,
        visibleTo: ['SUB_HAULER'],
      },
      {
        label: 'Original Sender',
        fieldType: 'readonly',
        fieldKey: 'original_sender',
        required: false,
        visibleTo: ['SUB_HAULER'],
      },
      // Recipient field
      {
        label: 'Third Party Sub Hauler',
        fieldType: 'search',
        fieldKey: 'third_party_sub_hauler_ids',
        required: true,
      },
    ],
    recipientLabel: 'Third Party Sub Hauler',
    recipientFieldKey: 'third_party_sub_hauler_ids',
    // Sub-hauler downstream always requires parent project
    parentProjectRequired: () => true,
  },
}

// Template configuration for Dispatches
export const DISPATCH_TEMPLATE_CONFIG: Record<
  ProjectTemplateRecipientType,
  {
    title: string
    requiredFields: string[]
    hiddenFields: string[]
    contactLabel: string
  }
> = {
  BROKER: {
    title: 'Dispatch for Broker',
    requiredFields: ['dispatchers'],
    hiddenFields: [],
    contactLabel: 'Broker',
  },
  THIRD_PARTY_BROKER: {
    title: 'Dispatch for Third Party Broker',
    requiredFields: ['dispatchers'],
    hiddenFields: [],
    contactLabel: 'Third Party Broker',
  },
  THIRD_PARTY_CONTRACTOR: {
    title: 'Dispatch for Third Party Contractor',
    requiredFields: ['dispatchers'],
    hiddenFields: [],
    contactLabel: 'Third Party Contractor',
  },
  SUB_HAULER: {
    title: 'Dispatch for Sub Hauler',
    requiredFields: ['subhaulers'],
    hiddenFields: [],
    contactLabel: 'Sub Hauler',
  },
  THIRD_PARTY_SUB_HAULER: {
    title: 'Dispatch for Third Party Sub Hauler',
    requiredFields: ['subhaulers'],
    hiddenFields: [],
    contactLabel: 'Third Party Sub Hauler',
  },
}

// Template configuration for Tags
export const TAG_TEMPLATE_CONFIG: Record<
  TagTemplateRecipientType,
  {
    title: string
    requiredFields: string[]
    hiddenFields: string[]
    contactLabel: string
  }
> = {
  BROKER_DRIVER: {
    title: "Tag for Broker's Driver",
    requiredFields: ['assigned_user'],
    hiddenFields: [],
    contactLabel: "Broker's Driver",
  },
  THIRD_PARTY_CONTRACTOR_DRIVER: {
    title: "Tag for Third Party Contractor's Driver",
    requiredFields: ['assigned_user'],
    hiddenFields: [],
    contactLabel: "Third Party Contractor's Driver",
  },
  CONTRACTOR_DRIVER: {
    title: "Tag for Contractor's Driver",
    requiredFields: ['assigned_user'],
    hiddenFields: [],
    contactLabel: "Contractor's Driver",
  },
  SUB_HAULER_DRIVER: {
    title: "Tag for Sub Hauler's Driver",
    requiredFields: ['assigned_user'],
    hiddenFields: [],
    contactLabel: "Sub Hauler's Driver",
  },
}

// Template options for dropdowns with visibility rules
export interface TemplateOption<T> {
  value: T
  label: string
  visibleTo?: SenderStakeholderType[]
}

export const PROJECT_TEMPLATE_OPTIONS: TemplateOption<ProjectTemplateRecipientType>[] =
  [
    // Fresh flow (no parent required) - available to contractors and brokers:
    {
      value: 'BROKER',
      label: 'Project Terms for Broker',
      visibleTo: ['CONTRACTOR', 'BROKER'],
    },
    // For brokers only - downstream flow:
    {
      value: 'THIRD_PARTY_BROKER',
      label: 'Project Terms for Third Party Broker',
      visibleTo: ['BROKER'],
    },
    // For contractors only:
    {
      value: 'THIRD_PARTY_CONTRACTOR',
      label: 'Project Terms for Third Party Contractor',
      // Brokers can also originate a contractor-bound PT (e.g. broker -> third party contractor)
      visibleTo: ['CONTRACTOR', 'BROKER'],
    },
    // For contractors and brokers:
    {
      value: 'SUB_HAULER',
      label: 'Project Terms for Sub Hauler',
      visibleTo: ['CONTRACTOR', 'BROKER'],
    },
    // For sub-haulers only - downstream flow (parent project required):
    {
      value: 'THIRD_PARTY_SUB_HAULER',
      label: 'Project Terms for Third Party Sub Hauler',
      visibleTo: ['SUB_HAULER'],
    },
  ]

// Helper function to filter template options by stakeholder type
export function getVisibleTemplateOptions(
  stakeholderType: SenderStakeholderType,
): TemplateOption<ProjectTemplateRecipientType>[] {
  return PROJECT_TEMPLATE_OPTIONS.filter(
    (option) => !option.visibleTo || option.visibleTo.includes(stakeholderType),
  )
}

// Get label for sub-hauler template based on sender stakeholder type
export function getSubHaulerTemplateLabel(
  stakeholderType: SenderStakeholderType,
): string {
  if (stakeholderType === 'SUB_HAULER') {
    return 'Sub-Contract Terms'
  }
  return 'Project Terms for Sub Hauler'
}

export const DISPATCH_TEMPLATE_OPTIONS: {
  value: ProjectTemplateRecipientType
  label: string
}[] = [
  { value: 'BROKER', label: 'Dispatch for Broker' },
  { value: 'THIRD_PARTY_BROKER', label: 'Dispatch for Third Party Broker' },
  {
    value: 'THIRD_PARTY_CONTRACTOR',
    label: 'Dispatch for Third Party Contractor',
  },
  { value: 'SUB_HAULER', label: 'Dispatch for Sub Hauler' },
  {
    value: 'THIRD_PARTY_SUB_HAULER',
    label: 'Dispatch for Third Party Sub Hauler',
  },
]

export const TAG_TEMPLATE_OPTIONS: {
  value: TagTemplateRecipientType
  label: string
}[] = [
  { value: 'BROKER_DRIVER', label: "Tag for Broker's Driver" },
  {
    value: 'THIRD_PARTY_CONTRACTOR_DRIVER',
    label: "Tag for Third Party Contractor's Driver",
  },
  { value: 'CONTRACTOR_DRIVER', label: "Tag for Contractor's Driver" },
  { value: 'SUB_HAULER_DRIVER', label: "Tag for Sub Hauler's Driver" },
]

// Fields that are immutable for downstream (sub-hauler to sub-hauler) projects
export const DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS = [
  'pickup_address',
  'dropoff_address',
  'job_site',
  'job_id',
  'po_number',
  'third_party_location',
] as const

// Fields that are editable for downstream projects
// These are pre-populated from parent but can be changed
export const DOWNSTREAM_PROJECT_EDITABLE_FIELDS = [
  'material',
  'truck_types',
  'pay_rate',
  'starting_date',
  'completion_date',
  'trucks_number',
  'payment_type',
  'payment_schedule',
  'notes',
  'rate_type',
  'volume',
  'invoice_interval',
  'status_interval',
] as const
